import { last } from 'lodash/fp';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { invariant } from '@react-dnd/invariant';
import { DragDropActions, DragDropMonitor, Backend, Identifier, XYCoord, DragDropManager, Unsubscribe } from 'dnd-core';
import { DragSourceOptions, EventName, ListenerType, TouchBackendOptions } from './interfaces';
import {
    eventShouldStartDrag,
    eventShouldEndDrag,
    isTouchEvent,
    isApplePencilTouchMove,
    isSmallStylusMovement,
    eventShouldDragFromTarget,
} from './utils/predicates';
import { getEventClientOffset, getNodeClientOffset } from './utils/offsets';
import supportsPassive from './utils/supportsPassive';
import { OptionsReader } from './OptionsReader';

// Utils
import { distance, inAngleRanges, translatePoint } from './utils/math';
import getDomElementAncestors from '../../../client/utils/dom/getDomElementAncestors';

// Constants
import { DOM_TAG_NAMES } from '../../../client/utils/domConstants';
import dragAndDropState, {
    clearPerformingLongHoverNavigation,
} from '../../../client/element/dnd/dragAndDropStateSingleton';

const DRAG_FEEDBACK_DELAY = 100;
const DELAY_MOVEMENT_THRESHOLD = 10;

const eventNames: Record<ListenerType, EventName> = {
    [ListenerType.mouse]: {
        start: 'mousedown',
        move: 'mousemove',
        end: 'mouseup',
        contextmenu: 'contextmenu',
    },
    [ListenerType.touch]: {
        start: 'touchstart',
        move: 'touchmove',
        end: 'touchend',
        cancel: 'touchcancel',
    },
    [ListenerType.keyboard]: {
        keydown: 'keydown',
    },
};

export default class TouchBackend implements Backend {
    private options: OptionsReader;

    // React-DnD Dependencies
    private actions: DragDropActions;
    private monitor: DragDropMonitor;

    // Internal State
    private static isSetUp: boolean;
    private sourceNodes: Record<Identifier, HTMLElement>;
    private sourceOptions: Record<Identifier, DragSourceOptions>;
    private sourcePreviewNodes: Record<string, HTMLElement>;
    private sourcePreviewNodeOptions: Record<string, {}>;
    private targetNodes: Record<string, HTMLElement>;
    private _mouseClientOffset: Partial<XYCoord>;
    private _moveStartClientOffset: Partial<XYCoord>;
    private _isScrolling: boolean;
    private listenerTypes: ListenerType[];
    private moveStartSourceIds: string[] | undefined;
    private _waitingForDelay: boolean | undefined;
    private timeout: ReturnType<typeof setTimeout> | undefined;
    private dragOverTargetIds: string[] | undefined;
    private draggedSourceNode: HTMLElement | undefined;
    private draggedSourceNodeRemovalObserver: MutationObserver | undefined;
    private _viewportScroll: Partial<XYCoord>;
    private _isScrollingResetTimeout: ReturnType<typeof setTimeout> | undefined;

    // Patch for iOS 13, discussion over #1585
    private lastTargetTouchFallback: Touch | undefined;

    // MILANOTE - use the HTML5Backend to support native drag and drop
    private html5Backend: Backend;

    public constructor(manager: DragDropManager, context: any, options: TouchBackendOptions) {
        this.options = new OptionsReader(options, context);
        this.actions = manager.getActions();
        this.monitor = manager.getMonitor();

        this.sourceNodes = {};
        this.sourceOptions = {};
        this.sourcePreviewNodes = {};
        this.sourcePreviewNodeOptions = {};
        this.targetNodes = {};
        this.listenerTypes = [];
        this._mouseClientOffset = {};
        this._moveStartClientOffset = {};
        this._isScrolling = false;
        this._waitingForDelay = false;

        // Caching the viewport offset to avoid layout thrashing
        // (see https://gist.github.com/paulirish/5d52fb081b3570c81e3a)
        this._viewportScroll = { x: 0, y: 0 };

        if (this.options.enableMouseEvents) {
            this.listenerTypes.push(ListenerType.mouse);
        }

        if (this.options.enableTouchEvents) {
            this.listenerTypes.push(ListenerType.touch);
        }

        if (this.options.enableKeyboardEvents) {
            this.listenerTypes.push(ListenerType.keyboard);
        }

        // iPad native file drag and drop support
        this.html5Backend = HTML5Backend(manager, context);
    }

    // public for test
    public get window() {
        return this.options.window;
    }

    // public for test
    public get document() {
        if (this.window) {
            return this.window.document;
        }
        return undefined;
    }

    public setup() {
        if (!this.window) {
            return;
        }

        invariant(!TouchBackend.isSetUp, 'Cannot have two Touch backends at the same time.');
        TouchBackend.isSetUp = true;

        this.addEventListener(this.window, 'start', this.handleTopMoveStart);
        this.addEventListener(this.window, 'start', this.handleTopMoveStartCapture, true);
        this.addEventListener(this.window, 'move', this.handleTopMove);
        this.addEventListener(this.window, 'end', this.handleTopMoveEndCapture, true);
        this.addEventListener(this.window, 'cancel', this.handleTopMoveEndCapture, true);

        if (this.options.enableMouseEvents && !this.options.ignoreContextMenu) {
            this.addEventListener(this.window, 'contextmenu', this.handleTopMoveEndCapture);
        }

        if (this.options.enableKeyboardEvents) {
            this.addEventListener(this.window, 'keydown', this.handleCancelOnEscape, true);
        }

        /**
         * If a scroll begins while we're waiting for a drag to start, stop the drag from occurring.
         */
        // MILANOTE MODIFICATION
        // NOTE: The use of this.window.addEventListener here, instead of this.addEventListener, is intentional
        //  because we want a single scroll event listener, regardless of whether both the mouse and touch
        //  listeners are enabled.
        this.window.addEventListener('scroll', this.handleOnScroll, { passive: true, capture: true });

        // iPad native file drag and drop support
        this.html5Backend.setup();
    }

    public teardown() {
        if (!this.window) {
            return;
        }

        TouchBackend.isSetUp = false;
        this._mouseClientOffset = {};

        this.removeEventListener(this.window, 'start', this.handleTopMoveStartCapture, true);
        this.removeEventListener(this.window, 'start', this.handleTopMoveStart);
        this.removeEventListener(this.window, 'move', this.handleTopMove);
        this.removeEventListener(this.window, 'end', this.handleTopMoveEndCapture, true);
        this.removeEventListener(this.window, 'cancel', this.handleTopMoveEndCapture, true);

        if (this.options.enableMouseEvents && !this.options.ignoreContextMenu) {
            this.removeEventListener(this.window, 'contextmenu', this.handleTopMoveEndCapture);
        }

        if (this.options.enableKeyboardEvents) {
            this.removeEventListener(this.window, 'keydown', this.handleCancelOnEscape, true);
        }

        this.uninstallSourceNodeRemovalObserver();

        this.resetTimeoutState();

        // iPad native file drag and drop support
        this.html5Backend.teardown();
    }

    /**
     * Generate profiling statistics for the Touch backends
     */
    public profile(): Record<string, number> {
        return {
            sourceNodes: Object.keys(this.sourceNodes).length,
            sourcePreviewNodes: Object.keys(this.sourcePreviewNodes).length,
            sourcePreviewNodeOptions: Object.keys(this.sourcePreviewNodeOptions).length,
            targetNodes: Object.keys(this.targetNodes).length,
            dragOverTargetIds: this.dragOverTargetIds?.length || 0,
        };
    }

    private addEventListener<K extends keyof EventName>(
        subject: HTMLElement | Window,
        event: K,
        handler: (e: any) => void,
        capture?: boolean,
    ) {
        const options = supportsPassive ? { capture, passive: false } : capture;

        this.listenerTypes.forEach(function (listenerType) {
            const evt = eventNames[listenerType][event];

            if (evt) {
                subject.addEventListener(evt, handler, options);
            }
        });
    }

    private removeEventListener<K extends keyof EventName>(
        subject: HTMLElement | Window,
        event: K,
        handler: (e: any) => void,
        capture?: boolean,
    ) {
        const options = supportsPassive ? { capture, passive: false } : capture;

        this.listenerTypes.forEach(function (listenerType) {
            const evt = eventNames[listenerType][event];

            if (evt) {
                subject.removeEventListener(evt, handler, options);
            }
        });
    }

    public connectDragSource(sourceId: string, node: HTMLElement, options = {}) {
        const handleMoveStart = this.handleMoveStart.bind(this, sourceId);

        this.sourceNodes[sourceId] = node;
        this.sourceOptions[sourceId] = options;

        this.addEventListener(node, 'start', handleMoveStart);

        return () => {
            delete this.sourceNodes[sourceId];
            delete this.sourceOptions[sourceId];
            this.removeEventListener(node, 'start', handleMoveStart);
        };
    }

    public connectDragPreview(sourceId: string, node: HTMLElement, options: any) {
        this.sourcePreviewNodeOptions[sourceId] = options;
        this.sourcePreviewNodes[sourceId] = node;

        return () => {
            delete this.sourcePreviewNodes[sourceId];
            delete this.sourcePreviewNodeOptions[sourceId];
        };
    }

    public connectDropTarget(targetId: string, node: HTMLElement): Unsubscribe {
        if (!this.document) {
            return () => null;
        }

        /**
         * Attaching the event listener to the body so that touchmove will work while dragging over multiple target elements.
         */
        this.targetNodes[targetId] = node;

        // MILANOTE ADDITION - Add the target ID to the DOM node's dataset, so we can retrieve it via the
        // handleTopMove method's `elementsFromPoint` rather than needing to map hovered IDs to hovered nodes.
        // Thus this allows us to delete the 'move' event listener that's added for each drop target.
        node.dataset.reactDndTargetId = targetId;

        // iPad native file drag and drop support
        const removeHtml5DropTarget = this.html5Backend.connectDropTarget(targetId, node);

        return () => {
            if (this.document) {
                delete this.targetNodes[targetId];
            }

            removeHtml5DropTarget && removeHtml5DropTarget();
        };
    }

    private getSourceClientOffset = (sourceId: string) => {
        const nodeOffset = getNodeClientOffset(this.sourceNodes[sourceId]) || { x: 0, y: 0 };

        return translatePoint(
            nodeOffset,
            // Translate into the current viewport's offset to account for times when the virtual keyboard
            // shows and hides. NOTE: this is cached to avoid layout thrashing.
            this._viewportScroll,
        );
    };

    private handleTopMoveStartCapture = (e: Event) => {
        if (!eventShouldStartDrag(e)) {
            return;
        }

        this.moveStartSourceIds = [];
    };

    private handleMoveStart = (sourceId: string) => {
        // Just because we received an event doesn't necessarily mean we need to collect drag sources.
        // We only collect start collecting drag sources on touch and left mouse events.
        if (Array.isArray(this.moveStartSourceIds)) {
            this.moveStartSourceIds.unshift(sourceId);
        }
    };

    private handleTopMoveStart = (event: MouseEvent | TouchEvent) => {
        // Disable movement if there are multiple touches active.
        // Also cancel any existing drags or timers if so
        if (event instanceof TouchEvent && event.touches.length > 1) {
            this.resetTimeoutState();

            if (this.monitor.isDragging()) {
                this.actions.endDrag();
            }

            return;
        }

        // There was a bug where a "touchstart" event was firing after a delayed touch move
        // was triggered. This caused the "this.waitingForDelay" property to be set which in turn
        // caused the "touchmove" actions to be ignored.
        // This is potentially due to the drag layer element moving to top, or changes in the touched
        // element due to drag mode, not 100% sure, but this change ensures that when a drag is in
        // progress another drag won't be started
        if (this.monitor?.isDragging()) return;

        // Get the top moved source
        const movedSourceId = last(this.moveStartSourceIds);
        if (!movedSourceId) return;

        this._moveStartClientOffset = getEventClientOffset(event) || {};

        const options = this.sourceOptions[movedSourceId];
        const dragStartDelay = options?.delayTouchStart || 0;
        const dragAngleRanges = options?.dragAngleRanges;

        // If the event is a mouse event, and we're allowing mouse events, then start the drag immediately
        // don't wait for the delay / drag angle ranges
        if (event.type === 'mousedown' && this.options.enableMouseEvents) {
            return this.handleTopMoveStartImmediately(event, 0);
        }

        // if we have no delay, and no dragAngleRanges, then can start the drag immediately
        if (dragStartDelay === 0 && !dragAngleRanges) {
            return this.handleTopMoveStartImmediately(event, 0);
        }

        // otherwise, need to wait for either the delay to pass, or the drag angle to be within the range
        this.handleTopMoveStartDelay(event, dragStartDelay);
    };

    private handleTopMoveStartImmediately = (event: MouseEvent | TouchEvent, timeSpentWaiting: number) => {
        if (!eventShouldStartDrag(event)) return;
        if (this._isScrolling) return;

        // If the target has been removed since the click/tap, don't start the drag
        // This may happen when clicking/tapping on draggable items that can cause location changes, like double tapping a board
        const targetRendered = event.target instanceof Node && document.body.contains(event.target);
        if (!targetRendered) return;

        // Don't prematurely preventDefault() here since it might:
        // 1. Mess up scrolling
        // 2. Mess up long tap (which brings up context menu)
        // 3. If there's an anchor link as a child, tap won't be triggered on link

        const clientOffset = getEventClientOffset(event);
        if (clientOffset) {
            if (isTouchEvent(event)) {
                this.lastTargetTouchFallback = event.targetTouches[0];
            }
            this._mouseClientOffset = clientOffset;
        }
        this._waitingForDelay = false;

        // In case the user is trying to click on a link, we don't want to start the scrolling too soon
        // otherwise they won't be able to.
        // So wait at least 100ms and if they haven't left the page due to clicking on a URL, or already started
        // moving the drag source, then begin the drag so that the user has visual feedback that the drag
        // has now been started.
        const feedbackDelay = Math.max(0, DRAG_FEEDBACK_DELAY - timeSpentWaiting);

        const startDragging = () => {
            const { moveStartSourceIds } = this;

            this.timeout = undefined;
            this._waitingForDelay = false;

            if (!moveStartSourceIds || this.monitor.isDragging()) return;

            this.moveStartSourceIds = undefined;

            this.actions.beginDrag(moveStartSourceIds, {
                clientOffset: translatePoint(this._mouseClientOffset, this._viewportScroll),
                getSourceClientOffset: this.getSourceClientOffset,
                publishSource: false,
            });

            this.actions.publishDragSource();
        };

        if (feedbackDelay === 0) {
            startDragging();
            return;
        }

        this.timeout = setTimeout(startDragging, feedbackDelay);
    };

    private handleTopMoveStartDelay = (e: Event, delay: number) => {
        if (!eventShouldStartDrag(e) || this._waitingForDelay) {
            return;
        }

        this.timeout = setTimeout(
            this.handleTopMoveStartImmediately.bind(this, e as any, delay),
            delay,
        ) as any as ReturnType<typeof setTimeout>;
        this._waitingForDelay = true;
    };

    private handleTopMove = (event: TouchEvent | MouseEvent) => {
        if (this._isScrolling) return;

        if (!eventShouldDragFromTarget(event)) {
            event.preventDefault();
            return;
        }

        if (this.monitor.isDragging()) {
            return this.handleTopMoveDrag(event);
        }

        return this.handleTopMoveBeforeDragStarted(event);
    };

    /**
     * Before we've started moving the drag source, we need to handle the move events to determine if we
     * should start the drag. This handles picking up the drag source when the user has moved it a little, and we're not waiting for a delay.
     */
    private handleTopMoveBeforeDragStarted = (event: TouchEvent | MouseEvent) => {
        // Disable movement if there are multiple touches active.
        if (event instanceof TouchEvent && event.touches.length > 1) return;

        const clientOffset = getEventClientOffset(event, this.lastTargetTouchFallback);
        if (!clientOffset) return;

        // If the user has moved more than the threshold during the delay, cancel the drag
        if (this._waitingForDelay && clientOffset) {
            const { x: startX = 0, y: startY = 0 } = this._moveStartClientOffset;
            const xDistanceMoved = Math.abs(clientOffset.x - startX);
            const yDistanceMoved = Math.abs(clientOffset.y - startY);

            if (xDistanceMoved > DELAY_MOVEMENT_THRESHOLD || yDistanceMoved > DELAY_MOVEMENT_THRESHOLD) {
                this.resetTimeoutState();
                this._waitingForDelay = false;
                this.moveStartSourceIds = undefined;
                return;
            }
        }

        // If we're waiting for a delay, then bail — drag will be started on the delay timeout
        if (this._waitingForDelay) return;

        // If we're using the Apple Pencil, we want to ignore small movements that occur when the pencil jitters
        const isInitialPencilTouchMove =
            isApplePencilTouchMove(event) && isSmallStylusMovement(event, this._moveStartClientOffset);
        if (isInitialPencilTouchMove) return;

        // If the drag source has a dragAngleRanges option, check if the angle of the drag is within the range
        const draggedSourceId = (this.monitor.getSourceId() as string) || this.moveStartSourceIds?.[0];
        const options = draggedSourceId ? this.sourceOptions[draggedSourceId] : undefined;
        const dragAngleRanges = options?.dragAngleRanges;
        const withinAcceptedDragAngleRange = dragAngleRanges
            ? inAngleRanges(
                  this._mouseClientOffset.x || 0,
                  this._mouseClientOffset.y || 0,
                  clientOffset.x,
                  clientOffset.y,
                  dragAngleRanges,
              )
            : true;

        // If we're not dragging and we've moved a little, that counts as a drag start
        if (
            this.moveStartSourceIds &&
            this.moveStartSourceIds.length > 0 &&
            distance(this._mouseClientOffset.x || 0, this._mouseClientOffset.y || 0, clientOffset.x, clientOffset.y) >
                (this.options.touchSlop ? this.options.touchSlop : 0) &&
            withinAcceptedDragAngleRange
        ) {
            this.handleTopMoveStartImmediately(event, 0);
        }
    };

    /**
     * Once dragging, this handles the move events to determine if we're hovering over a drop target.
     */
    private handleTopMoveDrag(event: TouchEvent | MouseEvent) {
        const clientOffset = getEventClientOffset(event, this.lastTargetTouchFallback);
        if (!clientOffset) return;

        event.preventDefault();

        const sourceNode = this.sourceNodes[this.monitor.getSourceId() as string];
        this.installSourceNodeRemovalObserver(sourceNode);
        this.actions.publishDragSource();

        const orderedDragOverTargetIds = this.getDragOverTargetIds(clientOffset);

        this.actions.hover(orderedDragOverTargetIds, {
            clientOffset: translatePoint(clientOffset, this._viewportScroll),
        });
    }

    /**
     * Determine the drop targets that are currently being hovered over.
     */
    private getDragOverTargetIds = (clientOffset: XYCoord): string[] => {
        // Get the a ordered list of nodes that are touched by
        const elementsAtPoint = this.document.elementsFromPoint(clientOffset.x, clientOffset.y);
        const topElement = elementsAtPoint[0];

        const orderedDragOverTargetIds: string[] = [];
        for (const nodeId in elementsAtPoint) {
            // eslint-disable-next-line no-prototype-builtins
            if (!elementsAtPoint.hasOwnProperty(nodeId)) {
                continue;
            }

            let currentNode = elementsAtPoint[nodeId];
            const reactDndTargetId: string | undefined = currentNode.dataset.reactDndTargetId;

            // We're hovering a drop target if it has a react-dnd-target-id and it
            // contains the top-most element in its DOM hierarchy
            const containsTopElement = currentNode === topElement || currentNode.contains(topElement);

            const isHoveredDropTarget = reactDndTargetId && containsTopElement;

            if (isHoveredDropTarget) {
                orderedDragOverTargetIds.push(reactDndTargetId);
            }

            while (currentNode) {
                currentNode = currentNode.parentElement;
            }
        }

        // Reverse order because dnd-core reverse it before calling the DropTarget drop methods
        orderedDragOverTargetIds.reverse();

        return orderedDragOverTargetIds;
    };

    private handleTopMoveEndCapture = (e: Event) => {
        this.resetTimeoutState();

        this._isScrolling = false;
        this._waitingForDelay = false;
        this.lastTargetTouchFallback = undefined;

        clearPerformingLongHoverNavigation();

        if (!eventShouldEndDrag(e)) {
            return;
        }

        if (!this.monitor.isDragging() || this.monitor.didDrop()) {
            this.moveStartSourceIds = undefined;
            return;
        }

        e.preventDefault();

        this._mouseClientOffset = {};

        this.uninstallSourceNodeRemovalObserver();
        this.actions.drop();
        this.actions.endDrag();
    };

    private handleCancelOnEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.monitor.isDragging()) {
            this._mouseClientOffset = {};

            this.uninstallSourceNodeRemovalObserver();
            this.actions.endDrag();
        }
    };

    private handleOnScroll = (event: Event) => {
        // When the document scrolls we need to account for its translation when performing moves
        if (event.target === this.document) {
            // NOTE: Need to use scrollLeft and scrollTop on the documentElement here as for some reason
            //  the viewport offset isn't actually updated correctly on scroll
            this._viewportScroll = {
                x: this.document.documentElement.scrollLeft,
                y: this.document.documentElement.scrollTop,
            };
        }

        // When dragging the line edge points, a scroll event will occur on the
        // CanvasViewportReplica as it tries to replicate the scroll position of the Canvas.  We don't
        // want this scroll to end the dragging
        if ((event.target as HTMLElement)?.dataset?.ignoreTouchScroll) return;

        // There seems to be a race condition where the timeout in handleTopMoveStartImmediately
        // fires and then a scroll starts before the touchmove event occurs. This results in the
        // card being lifted, but the canvas scrolling behind it (and the card never moving).
        // So in that scenario - just end the dragging immediately.
        if (this.monitor?.isDragging() && !dragAndDropState.isPerformingLongHoverNavigation) {
            this.actions.endDrag();
        }

        this.resetTimeoutState();

        this._isScrolling = true;
        this.setScrollingResetTimeout();
    };

    /**
     * After a scroll event, set the scrolling state to false after a short delay.
     * This is to prevent the scroll event from cancelling a drag if the user tries to drag shortly after scrolling.
     */
    private setScrollingResetTimeout = () => {
        if (this._isScrollingResetTimeout) {
            clearTimeout(this._isScrollingResetTimeout);
            this._isScrollingResetTimeout = undefined;
        }

        this._isScrollingResetTimeout = setTimeout(() => {
            this._isScrolling = false;
        }, 100);
    };

    /**
     * There seems to be a bug on iPads where if the source of a touch event gets removed from the DOM,
     * the touchmove events stop firing.  This can be circumvented by re-adding the node to the DOM
     * after it's been removed.
     * So this logic adds a mutation observer to the source node to see if it or its parents get removed
     * from the DOM, and if so it adds the source node to the DOM again.
     */
    private installSourceNodeRemovalObserver(node: HTMLElement | undefined) {
        // Only attach the mutation observer if we haven't already done so
        if (this.draggedSourceNodeRemovalObserver) return;

        // Keep a reference to the drag source node so that on source node removal we can re-add it to the DOM
        this.draggedSourceNode = node;

        this.draggedSourceNodeRemovalObserver = new MutationObserver((mutationList) => {
            const domAncestors = getDomElementAncestors(node);
            const lastNode = last(domAncestors);

            if (node && lastNode.tagName !== DOM_TAG_NAMES.HTML) {
                this.resurrectSourceNode();
                this.uninstallSourceNodeRemovalObserver();
            }
        });

        if (!node || !node.parentElement) {
            return;
        }

        const domAncestors = getDomElementAncestors(node);

        for (const ancestor of domAncestors) {
            if (ancestor === node) continue;

            this.draggedSourceNodeRemovalObserver.observe(ancestor, {
                childList: true,
            });
        }
    }

    /**
     * Adds the dragged source to the DOM again if it was removed, as this is important on iPads
     * to ensure the touchmove events continue to fire.
     */
    private resurrectSourceNode() {
        if (this.document && this.draggedSourceNode) {
            this.draggedSourceNode.style.display = 'none';
            this.draggedSourceNode.removeAttribute('data-reactid');
            this.document.body.appendChild(this.draggedSourceNode);
        }
    }

    /**
     * Removes the mutation observer from the dragged source node.
     */
    private uninstallSourceNodeRemovalObserver() {
        if (this.draggedSourceNodeRemovalObserver) {
            this.draggedSourceNodeRemovalObserver?.disconnect();
        }

        this.draggedSourceNodeRemovalObserver = undefined;
        this.draggedSourceNode = undefined;
    }

    private resetTimeoutState = () => {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = undefined;
            this._waitingForDelay = false;
        }
    };
}
